import React, { useState } from 'react';
import AdminSidebar from './components/AdminSidebar';
import Header from '../teacher_screen/components/Header';
import {
    DollarSign,
    TrendingUp,
    Download,
    Filter,
    Plus,
    MoreHorizontal,
    Search,
    CreditCard,
    ArrowUpRight,
    ArrowDownRight,
    CheckCircle2,
    Clock,
    AlertCircle
} from 'lucide-react';

export default function FeeManagement() {
    const transactions = [
        { id: '#TR-8291', student: 'Alice Thompson', date: 'Mar 07, 2026', amount: '$1,200', type: 'Tuition Fee', status: 'Success' },
        { id: '#TR-8289', student: 'David Brown', date: 'Mar 05, 2026', amount: '$450', type: 'Transport', status: 'Pending' },
        { id: '#TR-8285', student: 'John Doe', date: 'Mar 02, 2026', amount: '$1,200', type: 'Tuition Fee', status: 'Success' },
        { id: '#TR-8280', student: 'Michael Scott', date: 'Feb 28, 2026', amount: '$200', type: 'Exam Fee', status: 'Failed' },
        { id: '#TR-8276', student: 'Sarah Wilson', date: 'Feb 25, 2026', amount: '$1,200', type: 'Tuition Fee', status: 'Success' },
    ];

    return (
        <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans">
            <AdminSidebar />

            <main className="flex-1 overflow-y-auto px-8">
                <Header
                    title="Financial Control"
                    subtitle="Track revenue, manage fee structures and view transactions"
                >
                    <div className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-xl text-[14px] font-bold shadow-lg shadow-blue-100 hover:bg-blue-600 transition-all">
                        <Plus size={18} />
                        Invoice Student
                    </div>
                </Header>

                {/* Revenue Overview Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                    <div className="bg-[#111827] text-white p-8 rounded-[32px] shadow-2xl relative overflow-hidden group">
                        <div className="absolute -right-4 -bottom-4 opacity-10 scale-150 rotate-12 transition-transform group-hover:rotate-0">
                            <DollarSign size={160} />
                        </div>
                        <div className="relative z-10">
                            <p className="text-gray-400 text-[12px] font-black uppercase tracking-widest mb-2">Collected This Term</p>
                            <h3 className="text-[42px] font-black mb-4 tracking-tighter">$142,800.00</h3>
                            <div className="flex items-center gap-2 text-emerald-400 font-bold text-[14px]">
                                <ArrowUpRight size={18} />
                                +18.4% growth since last session
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm flex flex-col justify-center">
                        <p className="text-gray-400 text-[12px] font-black uppercase tracking-widest mb-2">Total Outstanding</p>
                        <h3 className="text-[36px] font-black mb-4 text-rose-500">$24,200.00</h3>
                        <div className="flex items-center gap-2 text-rose-400 font-bold text-[14px]">
                            <AlertCircle size={18} />
                            42 students with pending dues
                        </div>
                    </div>

                    <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm flex flex-col justify-center">
                        <p className="text-gray-400 text-[12px] font-black uppercase tracking-widest mb-2">Operational Expense</p>
                        <h3 className="text-[36px] font-black mb-4 text-[#111827]">$58,400.00</h3>
                        <div className="flex items-center gap-2 text-blue-500 font-bold text-[14px]">
                            <Clock size={18} />
                            Next payroll in 05 days
                        </div>
                    </div>
                </div>

                {/* Recent Transactions Table */}
                <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden flex flex-col mb-10 pb-4">
                    <div className="p-8 flex items-center justify-between border-b border-gray-50">
                        <div className="flex items-center gap-6">
                            <h3 className="text-[20px] font-black text-[#111827]">Recent Transactions</h3>
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 bg-blue-500 rounded-full"></span>
                                <span className="text-gray-400 font-bold text-[14px]">Live Updates</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <div className="relative group">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500" />
                                <input
                                    type="text"
                                    placeholder="Transaction ID..."
                                    className="pl-9 pr-4 py-2 bg-slate-50 border border-gray-50 rounded-xl text-[13px] font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all w-48"
                                />
                            </div>
                            <button className="p-2 text-gray-400 hover:text-gray-900 border border-transparent hover:border-gray-100 rounded-lg"><Download size={20} /></button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-[#F8FAFC]">
                                <tr>
                                    <th className="px-8 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase">Tx ID</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase">Student Name</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase">Date</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase">Fee Type</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase">Amount</th>
                                    <th className="px-8 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((v, i) => (
                                    <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-slate-50 transition-all group">
                                        <td className="px-8 py-5 text-[13px] font-black text-blue-500">{v.id}</td>
                                        <td className="px-6 py-5 text-[14px] font-black text-[#111827]">{v.student}</td>
                                        <td className="px-6 py-5 text-[13px] font-bold text-gray-400">{v.date}</td>
                                        <td className="px-6 py-5">
                                            <span className="px-3 py-1 bg-slate-100 text-gray-600 rounded-lg text-[11px] font-black uppercase tracking-widest">{v.type}</span>
                                        </td>
                                        <td className="px-6 py-5 text-[15px] font-black text-[#111827]">{v.amount}</td>
                                        <td className="px-8 py-5 text-right">
                                            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-widest ${v.status === 'Success' ? 'text-emerald-500 bg-emerald-50' :
                                                    v.status === 'Pending' ? 'text-amber-500 bg-amber-50' : 'text-rose-500 bg-rose-50'
                                                }`}>
                                                {v.status === 'Success' ? <CheckCircle2 size={12} /> : v.status === 'Pending' ? <Clock size={12} /> : <AlertCircle size={12} />}
                                                {v.status}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
}
